import * as React from 'react';
import ProductModel, { ItemForExcel } from '../../domain/Product';
import { connect } from 'react-redux';
import ApplicationState from '../../application/core/State';
import { default as DispatchProps, mapDispatchToProps } from '../../application/core/DispatchProps';
import EstimationsGetRequested from '../../application/estimations/events/EstimationsRequested';
import EstimationsToolbar from './EstimationsToolbar';
import TableHeader from '../TableHeader';
import EstimationsDetailsHeader from './EstimationsDetailsHeader';
import EstimationsFooter from './EstimationsFooter';
import EstimationPeriod from '../../domain/EstimationPeriod';
import { default as EstimationsModel } from '../../domain/estimations/Estimations';
import Product from '../Product';
import Estimation from './Estimation';
import { createDataForExcel } from './export/createDataForExcel';
import './export/ExcelButtons.css';
import ExportExcel from '../common/ExportExcel';
import ImportEstimationsExcel from '../common/ImportExcel';
import triggerClickOnExcelDownload from '../triggerClickOnExcelDownload';
import Channels from '../channels/Channels';
import EstimationsFilterSelected from '../../application/order/events/EstimationsFilterSelected';
import EstimationsFilterRemoved from '../../application/order/events/EstimationsFilterRemoved';
import { Filter } from '../../domain/Filter';
import { getBackend } from '../../application/core/createStore';
import api from '../../application/core/api';
import { AjaxResponse } from 'rxjs/internal/observable/dom/AjaxObservable';
import DataExportRequested from '../../application/products/events/DataExportRequested';
import DataExportSucceeded from '../../application/products/events/DataExportSucceeded';
import Loader from '../common/Loader';
import DataExportFailed from '../../application/products/events/DataExportFailed';
import { catchError } from 'rxjs/internal/operators';
import { of } from 'rxjs/index';
import createMapper from './export/createMapper';
import { EstimationColumn, estimationColumnsNeededForImport } from '../common/excelColumns';
import EstimationsImportRequested from '../../application/estimations/events/EstimationsImportRequested';
import extractOnlyNeededColumns from '../common/extractOnlyNeededColumns';
import excelColumn from '../common/excelColumns';
import ProductsNavigation from '../common/ProductsNavigation';
import { getEstimationNextCursor, getEstimationPreviousCursor } from '../../application/products/selector';
import EstimationProductsNextRequested from '../../application/products/events/EstimationProductsNextRequested';
import EstimationProductsPrevRequested from '../../application/products/events/EstimationProductsPrevRequested';

interface StateProps {
    products: ProductModel[];
    estimations: EstimationsModel;
    estimationPeriod?: EstimationPeriod;
    discountRate: number;
    filters: Filter[];
    productsLoader: boolean;
    nextCursor?: string;
    prevCursor?: string;
    cursorIndex: number;
}

interface State {
    spreadsheetView: boolean;
    spreadsheetData: ItemForExcel[];
}

class Estimations extends React.PureComponent<StateProps & DispatchProps, State> {
    constructor(props: DispatchProps & StateProps) {
        super(props);
        this.state = {
            spreadsheetView: false,
            spreadsheetData: []
        };
    }

    public componentDidMount(): void {
        this.props.dispatch(new EstimationsGetRequested());
    }

    public render(): JSX.Element {
        const headers = ['', 'Name', 'Package', 'Price €', 'Article ID'];
        return this.props.estimationPeriod && this.props.estimationPeriod.isOpen ? (
            <div className="estimations">
                <EstimationsToolbar />
                <div className="current-estimation-wrapper">
                    <Channels
                        channelSelected={this.channelSelected}
                        channelDeSelected={this.channelDeSelected}
                        selectedNode={this.getSelectedNode()}
                        color="estimations-blue"
                    />

                    {!this.props.estimationPeriod && <Loader />}

                    {!this.state.spreadsheetView ? (
                        <div className="current-estimation-products">
                            {this.props.productsLoader && <Loader />}
                            <div className="current-estimation-header">
                                <TableHeader headers={headers} class={'estimations-product-header'} />
                                <div className="estimations-details-wrapper">
                                    <div className="estimations-excel-wrapper">
                                        <ImportEstimationsExcel
                                            products={this.props.products}
                                            name="Estimations"
                                            importExcel={this.onImport}
                                        />
                                        <ExportExcel
                                            onDownloadClicked={this.onExportClicked}
                                            spreadsheetData={this.state.spreadsheetData}
                                            columns={createMapper(this.props.estimationPeriod.months)}
                                            name="Estimations"
                                        />
                                    </div>
                                    <EstimationsDetailsHeader />
                                </div>
                            </div>
                            <div className="current-estimation-list">
                                {this.props.products.map((product: ProductModel) => (
                                    <div className="current-order-container" key={product.articleId}>
                                        <Product product={product} key={product.articleId} userDiscount={this.props.discountRate} />
                                        <Estimation product={product} userDiscount={this.props.discountRate} />
                                    </div>
                                ))}
                                <ProductsNavigation
                                    cursorIndex={this.props.cursorIndex}
                                    nextCursor={this.props.nextCursor}
                                    prevCursor={this.props.prevCursor}
                                    next={this.nextProducts}
                                    prev={this.prevProducts}
                                />
                            </div>
                        </div>
                    ) : (
                        <div>Estimations spreadsheet</div>
                    )}
                </div>
                <EstimationsFooter />
            </div>
        ) : !this.props.estimationPeriod ? (
            <Loader />
        ) : (
            <span className="close-message">{this.props.estimationPeriod.closeMessage}</span>
        );
    }

    private onExportClicked = (): void => {
        this.props.dispatch(new DataExportRequested());
        getBackend()
            .get(`${api()}products/export`)
            .pipe(
                catchError(() => {
                    this.props.dispatch(new DataExportFailed());
                    return of(null);
                })
            )
            .subscribe((response: AjaxResponse | null) => {
                if (response !== null) {
                    const spreadsheetData = createDataForExcel(
                        response.response,
                        this.props.estimations,
                        this.props.estimationPeriod!.months
                    );
                    this.setState({ spreadsheetData }, () => {
                        triggerClickOnExcelDownload('estimations-download-button');
                        this.setState({ spreadsheetData: [] });
                        this.props.dispatch(new DataExportSucceeded());
                    });
                }
            });
    };

    private onImport = (rows: string[][]): void => {
        this.props.dispatch(
            new EstimationsImportRequested(extractOnlyNeededColumns<EstimationColumn>(estimationColumnsNeededForImport, rows, excelColumn))
        );
    };

    private channelSelected = (channelId: number): void =>
        this.props.dispatch(new EstimationsFilterSelected({ type: 'nodeCategory', value: channelId, single: true }));

    private channelDeSelected = (): void => this.props.dispatch(new EstimationsFilterRemoved({ type: 'nodeCategory' }));

    private getSelectedNode = (): number | undefined => {
        const filter = this.props.filters.find((f: Filter) => f.type === 'nodeCategory');
        return filter ? (filter.value as number) : -1;
    };
    private nextProducts = (): void => this.props.dispatch(new EstimationProductsNextRequested());
    private prevProducts = (): void => this.props.dispatch(new EstimationProductsPrevRequested());
}

const mapStateToProps = (state: ApplicationState): StateProps => ({
    products: state.products.estimationProducts,
    estimations: state.estimations.estimations,
    estimationPeriod: state.admin.estimationPeriod,
    discountRate: state.user.discountRate,
    filters: state.estimations.filters,
    productsLoader: state.products.productsLoader,
    nextCursor: getEstimationNextCursor(state.products),
    prevCursor: getEstimationPreviousCursor(state.products),
    cursorIndex: state.products.estimationProductCursorIndex
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(Estimations);



// WEBPACK FOOTER //
// ./src/view/estimations/Estimations.tsx